import { faInfoCircle, faThumbTack } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { InfoTooltip } from '@karnov/kui-react'

import { motion } from 'motion/react'

import { ComponentProps, FC } from 'react'

import { useTranslation } from 'react-i18next'

import { AccordionButton } from 'pages/Conversation/components/AccordionButton'

import { useIsAccordionOpen, useToggleAccordion } from 'stores/useAccordionsStateStore'
import { cn } from 'utils/cn'

import { ConversationItem } from './ConversationItem'
import { getGroupedConversationSortedByDate } from './utils'

type Props = {
  conversations: ReturnType<typeof getGroupedConversationSortedByDate>[0]
  showTooltips?: boolean
  isPinnedConversations?: boolean
  title: keyof ReturnType<typeof getGroupedConversationSortedByDate>
} & Omit<ComponentProps<typeof ConversationItem>, 'isPinned' | 'conversation'>

export const ConversationsGroupAccordion: FC<Props> = ({
  activeConversationId,
  conversations,
  isPinnedConversations,
  onSelectConversation,
  onClickDeleteConversation,
  onTriggerEvent,
  showTooltips = false,
  title,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'conversation_history',
  })

  const isAccordionOpen = useIsAccordionOpen({
    id: title,
    isOpenByDefault: true,
    namespace: 'conversations_group',
  })

  const toggleAccordion = useToggleAccordion({ isOpenByDefault: true, namespace: 'conversations_group' })

  return (
    <>
      <div className="my-2 flex flex-col" key={title}>
        <AccordionButton
          isAccordionOpen={isAccordionOpen}
          onClick={() => toggleAccordion(title)}
          isSmall
          isDimmed
        >
          {isPinnedConversations && <FontAwesomeIcon className="ml-0.5 mr-2 text-subdued" icon={faThumbTack} />}
          {title}
          {showTooltips && (
            <InfoTooltip content={t('retention_info')}>
              <FontAwesomeIcon size="sm" icon={faInfoCircle} className="ml-2 text-subdued" />
            </InfoTooltip>
          )}
        </AccordionButton>
        <motion.ul
          animate={isAccordionOpen ? 'open' : 'closed'}
          aria-expanded={isAccordionOpen}
          className={cn('flex flex-col overflow-visible print:overflow-y-auto print:!h-full print:!opacity-100 print:!block', {
            'overflow-hidden': !isAccordionOpen,
          })}
          initial="closed"
          transition={{
            bounce: 0,
            duration: 0.7,
            type: 'spring',
          }}
          variants={{
            closed: {
              height: 0,
              opacity: 0,
            },
            open: {
              height: 'auto',
              opacity: 1,
            },
          }}
        >
          {conversations.map(conversation => (
            <ConversationItem
              activeConversationId={activeConversationId}
              conversation={conversation}
              isPinned={isPinnedConversations}
              key={conversation.id}
              onSelectConversation={onSelectConversation}
              onClickDeleteConversation={onClickDeleteConversation}
              onTriggerEvent={onTriggerEvent}
            />
          ))}
        </motion.ul>
      </div>
    </>
  )
}
